"use client";

import React from "react";
import Logo from "@/src/components/layout/Logo";
import { Search } from "lucide-react";
import DesktopSearch from "@/src/components/layout/header/DesktopSearch";
import { type IFilter } from "@/src/shared/features/search";
import { useAppRouter } from "@/src/shared/hooks/useAppRouter";
import { isAppliedSmallerSearchBar } from "@/src/shared/utils/global";
import ProfileSettings from "@/src/components/layout/header/ProfileSettings";

interface IDesktopHeaderProps {
  headerState: "top" | "scrolled";
  isSearchActive: boolean;
  activateSearch: (section: "where" | "dates" | "guests") => void;
  filters: IFilter;
  setFilters: React.Dispatch<React.SetStateAction<IFilter>>;
  clearDestination: () => void;
  clearDates: () => void;
  clearGuests: () => void;
  getTotalGuests: () => string;
  activeSearchSection: "where" | "dates" | "guests" | null;
  setActiveSearchSection: React.Dispatch<
    React.SetStateAction<"where" | "dates" | "guests" | null>
  >;
  updateGuestCount: (
    type: keyof IFilter["guests"],
    op: "add" | "subtract",
  ) => void;
  handleSearch: (next?: IFilter) => void;
}

const DesktopHeader: React.FC<IDesktopHeaderProps> = (props) => {
  const {
    headerState,
    isSearchActive,
    activateSearch,
    filters,
    setFilters,
    clearDestination,
    clearDates,
    clearGuests,
    getTotalGuests,
    activeSearchSection,
    setActiveSearchSection,
    updateGuestCount,
    handleSearch,
  } = props;
  const { pathname, query } = useAppRouter();
  const queryText = (key: string) => {
    const value = query[key];
    return typeof value === "string" ? value : null;
  };

  const isSmallSearchBar = isAppliedSmallerSearchBar(pathname);

  const showCompactBar =
    (headerState === "scrolled" || isSmallSearchBar) && !isSearchActive;

  const whereLabel =
    filters.destination?.name || queryText("address_name") || "Location";
  const fromParam = queryText("from");
  const toParam = queryText("to");
  const checkIn = filters.checkIn || (fromParam ? new Date(fromParam) : null);
  const checkOut = filters.checkOut || (toParam ? new Date(toParam) : null);
  // Day-first short format (e.g. "25 May") via en-GB locale, matching the
  // rest of the app (formatShortDate in src/utilities/global.ts).
  const dateOptions: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
  };
  const datesLabel =
    checkIn && checkOut
      ? `${checkIn.toLocaleDateString("en-GB", dateOptions)} – ${checkOut.toLocaleDateString("en-GB", dateOptions)}`
      : "Date";
  const totalGuests =
    filters.guests.adults + filters.guests.children + filters.guests.infants;
  const guestsLabel =
    totalGuests > 0
      ? `${totalGuests} guest${totalGuests > 1 ? "s" : ""}`
      : "Guests";

  return (
    <div className="hidden md:block ">
      {/* Airbnb's header: across the page, 32px in, 48px from 1440px. */}
      <div className="flex h-16 items-center justify-between px-8 min-[1440px]:px-12">
        <div className="flex items-center">
          <Logo />
        </div>
        {/* Compact search bar */}
        <div className="flex-1 flex items-center justify-center">
          {/* Always mounted: it grows out of / back into the large bar (DesktopSearch) on
              the same 300ms curve, instead of popping in after the bar unmounts. */}
          <button
            aria-hidden={!showCompactBar}
            tabIndex={showCompactBar ? undefined : -1}
            className={`flex items-center bg-white border border-gray-300 rounded-full shadow-[0px_2px_12px_0px_rgba(0,0,0,0.2)] hover:shadow-md transition-[opacity,transform,visibility,box-shadow] duration-300 ease-out py-2 pl-6 pr-2 w-full max-w-[480px] min-w-[320px] ${
              showCompactBar
                ? "opacity-100 translate-y-0 scale-100"
                : "invisible pointer-events-none opacity-0 translate-y-6 scale-[1.4]"
            }`}
            type="button"
          >
            {/* Location Section */}
            <div
              className="flex items-center flex-1"
              onClick={() => activateSearch("where")}
            >
              <img
                src="/icons/map-pin.png"
                alt="Location"
                className="w-4 h-4 mr-2 flex-shrink-0"
              />
              <span className="text-sm font-medium capitalize truncate">
                {(whereLabel || "Location").length > 8
                  ? `${(whereLabel || "Location").substring(0, 8)}...`
                  : whereLabel || "Location"}
              </span>
            </div>

            <div className="w-px h-6 bg-gray-300 mx-3 flex-shrink-0" />

            {/* Date Section */}
            <div
              className="flex items-center flex-1 justify-center"
              onClick={() => activateSearch("dates")}
            >
              <img
                src="/icons/calendar.png"
                alt="Calendar"
                className="w-4 h-4 mr-2 flex-shrink-0"
              />
              <span className="text-sm font-medium truncate">
                {datesLabel || "Date"}
              </span>
            </div>

            <div className="w-px h-6 bg-gray-300 mx-3 flex-shrink-0" />

            {/* Guests Section */}
            <div
              className="flex items-center flex-1"
              onClick={() => activateSearch("guests")}
            >
              <img
                src="/icons/users.png"
                alt="Users"
                className="w-4 h-4 mr-2 flex-shrink-0"
              />
              <span className="text-sm text-ink-secondary truncate">
                {guestsLabel || "Guests"}
              </span>
            </div>

            {/* Search control: use div role=button to avoid nested <button> inside motion.button */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => activateSearch("guests")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  activateSearch("guests");
                }
              }}
              aria-label="Open search"
              className="ml-3 w-9 h-9 bg-brand-500 hover:bg-brand-600 rounded-full flex items-center justify-center flex-shrink-0 hover:scale-105 transition-transform cursor-pointer"
            >
              <Search size={14} className="text-white" />
            </div>
          </button>
        </div>
        <ProfileSettings />
      </div>

      {/* Only render DesktopSearch if not on details OR on details & active */}
      {(!isSmallSearchBar || isSearchActive) && (
        <DesktopSearch
          {...{
            headerState,
            isSearchActive,
            activateSearch,
            filters,
            setFilters,
            clearDestination,
            clearDates,
            clearGuests,
            getTotalGuests,
            activeSearchSection,
            setActiveSearchSection,
            updateGuestCount,
            handleSearch,
          }}
        />
      )}
    </div>
  );
};

export default DesktopHeader;
